
import { Input } from "@/components/ui/Input";
import { InputShell } from "@/components/ui/InputShell";
import { useMemo, useRef, useEffect } from "react";
import { Search } from "lucide-react";
import type { SceneObject } from "@mind-sands/shared";
import { useSceneStore } from "../stores/sceneStore";
import { useSelection } from "../hooks/useSelection";
import { SceneObjectListItem } from "./SceneObjectListItem";

// ─── Helpers ────────────────────────────────────────────────

function toDisplayName(objectId: string): string {
  return objectId
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Number duplicate objectIds: "Oak Tree · 2". Unique ids stay unnumbered. */
export function instanceDisplayNames(
  objects: readonly Pick<SceneObject, "id" | "objectId">[],
): Map<string, string> {
  const totals = new Map<string, number>();
  for (const obj of objects) {
    totals.set(obj.objectId, (totals.get(obj.objectId) ?? 0) + 1);
  }
  const seen = new Map<string, number>();
  const labels = new Map<string, string>();
  for (const obj of objects) {
    const name = toDisplayName(obj.objectId);
    const total = totals.get(obj.objectId) ?? 1;
    if (total <= 1) {
      labels.set(obj.id, name);
      continue;
    }
    const n = (seen.get(obj.objectId) ?? 0) + 1;
    seen.set(obj.objectId, n);
    labels.set(obj.id, `${name} · ${n}`);
  }
  return labels;
}

// ─── SceneObjectList ────────────────────────────────────────

interface SceneObjectListProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSelect: (id: string) => void;
}

export function SceneObjectList({
  searchQuery,
  onSearchChange,
  onSelect,
}: SceneObjectListProps) {
  const objects = useSceneStore((state) => state.scene.objects);
  const { selectedObjectId } = useSelection();
  const selectedItemRef = useRef<HTMLDivElement>(null);

  const labels = useMemo(() => instanceDisplayNames(objects), [objects]);

  const filteredObjects = useMemo(() => {
    if (!searchQuery.trim()) return objects;
    const query = searchQuery.toLowerCase();
    return objects.filter((obj) =>
      (labels.get(obj.id) ?? toDisplayName(obj.objectId))
        .toLowerCase()
        .includes(query),
    );
  }, [objects, searchQuery, labels]);

  useEffect(() => {
    if (selectedObjectId && selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedObjectId]);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Search box */}
      <div className="px-3 py-2 border-b border-sands-200 shrink-0">
        <InputShell className="px-2 py-1.5">
          <Search className="w-3.5 h-3.5 text-sands-400 shrink-0" />
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search objects..."
            accent="none"
            surface="ghost"
            borderWeight="none"
            inputSize="xs"
            className="flex-1 min-w-0 py-0 text-xs text-sands-700"
          />
        </InputShell>
      </div>

      {/* Object list */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {objects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 px-4">
            <p className="text-xs text-sands-400 text-center">
              场景中无模型
            </p>
          </div>
        ) : filteredObjects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 px-4">
            <p className="text-xs text-sands-400 text-center">
              未找到匹配的模型
            </p>
          </div>
        ) : (
          <div className="py-1">
            {filteredObjects.map((obj) => {
              const displayName =
                labels.get(obj.id) ?? toDisplayName(obj.objectId);
              return (
                <SceneObjectListItem
                  key={obj.id}
                  ref={obj.id === selectedObjectId ? selectedItemRef : undefined}
                  object={obj}
                  displayName={displayName}
                  isSelected={obj.id === selectedObjectId}
                  onSelect={onSelect}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
